import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, PORTAL_PATH, useAuth } from "@/lib/AuthContext";
import PublicLayout from "@/components/brand/PublicLayout";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { Loading } from "@/components/portal/Ui";

import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import Services from "@/pages/public/Services";
import Contact from "@/pages/public/Contact";
import JoinCreators from "@/pages/public/JoinCreators";
import InviteJoin from "@/pages/public/InviteJoin";

import Login from "@/pages/auth/Login";
import ResetPassword from "@/pages/auth/ResetPassword";

import Account from "@/pages/Account";
import CreatorCampaigns from "@/pages/creator/CreatorCampaigns";
import CreatorInvoices from "@/pages/creator/CreatorInvoices";
import ClientCampaigns from "@/pages/client/ClientCampaigns";
import ClientCampaignDetail from "@/pages/client/ClientCampaignDetail";
import InternalOverview from "@/pages/internal/InternalOverview";
import InternalCampaigns from "@/pages/internal/InternalCampaigns";
import CampaignsTracker from "@/pages/internal/CampaignsTracker";
import TrackerCampaign from "@/pages/internal/TrackerCampaign";
import UsersClients from "@/pages/internal/UsersClients";
import MediaLibrary from "@/pages/internal/MediaLibrary";
import CreatorApplications from "@/pages/internal/CreatorApplications";

const CREATOR_NAV = [
  { to: "/creator", label: "My Campaigns", end: true, testId: "creator-campaigns" },
  { to: "/creator/invoices", label: "My Invoices", testId: "creator-invoices" },
  { to: "/creator/account", label: "Account", testId: "creator-account" },
];

const CLIENT_NAV = [
  { to: "/client", label: "My Campaigns", end: true, testId: "client-campaigns" },
  { to: "/client/account", label: "Account", testId: "client-account" },
];

function internalNav(isAdmin) {
  const nav = [
    { to: "/internal", label: "Overview", end: true, testId: "internal-overview" },
    { to: "/internal/tracker", label: "Campaigns Tracker", testId: "internal-tracker" },
    { to: "/internal/campaigns", label: "Campaigns", testId: "internal-campaigns" },
    { to: "/internal/applications", label: "Creator Applications", testId: "internal-applications" },
    { to: "/internal/media", label: "Media Library", testId: "internal-media" },
    { to: "/internal/account", label: "Account", testId: "internal-account" },
  ];
  if (isAdmin)
    nav.splice(nav.length - 1, 0, {
      to: "/internal/users",
      label: "Users & Clients",
      testId: "internal-users",
    });
  return nav;
}

function RequireRole({ roles, children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loading label="Checking your access…" />;
  if (user === false) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (!roles.includes(user.role)) return <Navigate to={PORTAL_PATH[user.role] || "/"} replace />;
  return children;
}

function RedirectIfSignedIn({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading label="Checking your access…" />;
  if (user && user !== false) return <Navigate to={PORTAL_PATH[user.role] || "/"} replace />;
  return children;
}

function InternalShell() {
  const { user } = useAuth();
  return <PortalLayout nav={internalNav(user?.role === "admin")} title="Internal" />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/join" element={<JoinCreators />} />
            {/* retired pages — keep old links landing somewhere sensible */}
            <Route path="/approach" element={<Navigate to="/" replace />} />
            <Route path="/portfolio" element={<Navigate to="/" replace />} />
          </Route>

          <Route
            path="/login"
            element={
              <RedirectIfSignedIn>
                <Login />
              </RedirectIfSignedIn>
            }
          />
          <Route path="/invite/:token" element={<InviteJoin />} />
          <Route path="/accept-invite" element={<ResetPassword invite />} />
          <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
          <Route path="/reset-password" element={<Navigate to="/login" replace />} />

          <Route
            element={
              <RequireRole roles={["creator"]}>
                <PortalLayout nav={CREATOR_NAV} title="Creator" />
              </RequireRole>
            }
          >
            <Route path="/creator" element={<CreatorCampaigns />} />
            <Route path="/creator/invoices" element={<CreatorInvoices />} />
            <Route path="/creator/account" element={<Account />} />
          </Route>

          <Route
            element={
              <RequireRole roles={["client"]}>
                <PortalLayout nav={CLIENT_NAV} title="Client" />
              </RequireRole>
            }
          >
            <Route path="/client" element={<ClientCampaigns />} />
            <Route path="/client/campaigns" element={<ClientCampaigns />} />
            <Route path="/client/campaigns/:campaignId" element={<ClientCampaignDetail />} />
            <Route path="/client/account" element={<Account />} />
          </Route>

          <Route
            element={
              <RequireRole roles={["admin", "internal"]}>
                <InternalShell />
              </RequireRole>
            }
          >
            <Route path="/internal" element={<InternalOverview />} />
            <Route path="/internal/tracker" element={<CampaignsTracker />} />
            <Route path="/internal/tracker/campaign/:campaignId" element={<TrackerCampaign />} />
            <Route path="/internal/performance" element={<Navigate to="/internal/tracker" replace />} />
            <Route path="/internal/invoices" element={<Navigate to="/internal/tracker" replace />} />
            <Route path="/internal/campaigns" element={<InternalCampaigns />} />
            <Route path="/internal/applications" element={<CreatorApplications />} />
            <Route path="/internal/media" element={<MediaLibrary />} />
            <Route
              path="/internal/users"
              element={
                <RequireRole roles={["admin"]}>
                  <UsersClients />
                </RequireRole>
              }
            />
            <Route path="/internal/account" element={<Account />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#111111",
              color: "#ffffff",
              border: "1px solid #2a2a2a",
              fontFamily: "'DM Sans', sans-serif",
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}
